import { z } from "zod";

import { isAdwSchema } from "@/src/models/common-schema";
import { DmeDocumentType, DmeOrderType } from "@/src/models/constants/document-types";
import { COMPANY_NAME_OTHER_PROMPT, SIGNATURE_REQUIRED_PROMPT } from "@/src/models/constants/prompt";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { PriorAuthorizationStatus } from "@/src/models/documents/prior-authorization/prior-authorization";
import { locationSchema } from "@/src/models/location";
import { patientMinimalSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export const dmeTypeDisplay: Record<DmeDocumentType, string> = {
  [DmeDocumentType.DME_ORDER]: "DME Order",
  [DmeDocumentType.DIABETIC_SUPPLIES]: "DME Order - Diabetic Supplies",
  [DmeDocumentType.DME_REQUEST]: "DME Request",
  [DmeDocumentType.DME_RESPONSE]: "DME Response",
};

export const dmeDocumentTypeSchema = z.nativeEnum(DmeDocumentType);

const baseDmeFields = {
  patient: patientMinimalSchema,
  receivingLocation: locationSchema.nullable(),
};

const extendedDmeSchema = z.object({
  isAdw: isAdwSchema,
  ...DocumentDateSchema.shape,
  authorizationStatus: z.nativeEnum(PriorAuthorizationStatus).nullable(),
  orderNumber: z.string().nullable(),
  dmeType: z.string().nullable(),
  documentType: z.string().nullable(),
  dmeOrderType: z
    .nativeEnum(DmeOrderType)
    .nullable()
    .describe("Whether this is a CMN (Certificate of Medical Necessity) or a Setup order"),
  companyName: z.string().nullable().describe("The name of the DME supplier or company."),
  companyNameOther: z.string().nullable().describe(COMPANY_NAME_OTHER_PROMPT),
  location: locationSchema.nullable(),
  provider: practitionerSchema.nullable(),
  receivingProvider: practitionerSchema.nullable(),
  signatureRequired: z.boolean().nullable().describe(SIGNATURE_REQUIRED_PROMPT),
});

const dmeCustomFields: Record<DmeDocumentType, Record<string, z.ZodTypeAny>> = {
  [DmeDocumentType.DIABETIC_SUPPLIES]: extendedDmeSchema.pick({
    authorizationStatus: true,
    dateOfDocument: true,
    dateOfEvent: true,
    dateReceived: true,
    orderNumber: true,
    dmeType: true,
    receivingProvider: true,
  }).shape,
  [DmeDocumentType.DME_ORDER]: extendedDmeSchema.pick({
    isAdw: true,
    authorizationStatus: true,
    dateOfDocument: true,
    dateOfEvent: true,
    dateReceived: true,
    orderNumber: true,
    dmeType: true,
    documentType: true,
    dmeOrderType: true,
    companyName: true,
    companyNameOther: true,
    provider: true,
    receivingProvider: true,
    signatureRequired: true,
  }).shape,
  [DmeDocumentType.DME_REQUEST]: extendedDmeSchema.pick({
    authorizationStatus: true,
    dateOfDocument: true,
    dateOfEvent: true,
    dateReceived: true,
    orderNumber: true,
    dmeType: true,
  }).shape,
  [DmeDocumentType.DME_RESPONSE]: extendedDmeSchema.pick({
    authorizationStatus: true,
    dateOfDocument: true,
    dateOfEvent: true,
    dateReceived: true,
    dmeType: true,
  }).shape,
};

const makeDmeSchema = (type: DmeDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseDmeFields,
    ...customFields,
  });

export const dmeSchemas: Record<DmeDocumentType, z.ZodObject<any>> = (
  Object.values(DmeDocumentType) as DmeDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeDmeSchema(type, dmeCustomFields[type]);

    return acc;
  },
  {} as Record<DmeDocumentType, z.ZodObject<any>>
);

const dmeSchemasArray = (Object.values(DmeDocumentType) as DmeDocumentType[]).map((type) => dmeSchemas[type]) as [
  z.ZodObject<any>,
  ...z.ZodObject<any>[],
];

export const anyDmeSchema = z.discriminatedUnion("type", dmeSchemasArray);

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type Dme = ExtractedDocumentFields<DmeDocumentType, typeof extendedDmeSchema, typeof baseDmeFields> &
  DatabaseDefaults;

const now = new Date().toISOString();

export const dmeShape: Dme = {
  id: "",
  type: DmeDocumentType.DME_ORDER,
  patient: null,
  receivingLocation: null,
  orderNumber: null,
  dmeType: null,
  documentType: null,
  dmeOrderType: null,
  companyName: null,
  companyNameOther: null,
  authorizationStatus: null,
  dateOfDocument: null,
  dateOfEvent: null,
  dateReceived: null,
  provider: null,
  receivingProvider: null,
  signatureRequired: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
